/**
 * Marketing consent from Semantiko CMP, which GTM loads on aspab.com.
 *
 * The CMP exposes window.SemantikoCMP (getConsent/on) once its script has run
 * and stores the choice in localStorage under "semantiko_cmp_v1". Because GTM
 * loads it asynchronously, the stored choice is read directly until the API
 * exists. "pending" means the visitor has not made a choice yet.
 */

const CMP_STORAGE_KEY = "semantiko_cmp_v1";
const CMP_WAIT_MS = 30_000;
const CMP_POLL_MS = 250;

export type ConsentStatus = "accepted" | "declined" | "pending";

interface CmpState {
  marketing?: boolean;
}

interface SemantikoCMP {
  getConsent: () => CmpState | null;
  /** The event name is currently ignored by the CMP; every choice is emitted. */
  on: (event: string, callback: (state: CmpState) => void) => () => void;
}

declare global {
  interface Window {
    SemantikoCMP?: SemantikoCMP;
  }
}

function toStatus(state: CmpState | null | undefined): ConsentStatus {
  if (!state) return "pending";
  return state.marketing === true ? "accepted" : "declined";
}

function readStoredState(): CmpState | null {
  try {
    const raw = localStorage.getItem(CMP_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed.consentId === "string" && parsed.state ? parsed.state : null;
  } catch {
    return null;
  }
}

export function getConsent(): ConsentStatus {
  if (typeof window === "undefined") return "pending";
  try {
    const cmp = window.SemantikoCMP;
    if (cmp) return toStatus(cmp.getConsent());
  } catch {
    // Fall back to the stored choice.
  }
  return toStatus(readStoredState());
}

/** Calls listener whenever the visitor changes their choice in the CMP. */
export function onConsentChange(listener: (status: ConsentStatus) => void): () => void {
  let unsubscribe: (() => void) | undefined;
  let stopped = false;
  const startedAt = Date.now();

  const attach = () => {
    if (stopped) return;
    const cmp = window.SemantikoCMP;
    if (cmp) {
      try {
        unsubscribe = cmp.on("change", (state) => listener(toStatus(state)));
      } catch {
        // CMP without event support — the choice is still read on next load.
      }
      return;
    }
    if (Date.now() - startedAt < CMP_WAIT_MS) {
      window.setTimeout(attach, CMP_POLL_MS);
    }
  };
  attach();

  return () => {
    stopped = true;
    unsubscribe?.();
  };
}
